import React, { useEffect, useCallback, useMemo } from 'react';
import LoaderSpinner from '../common/Loader';
import { useNavigate, useParams } from 'react-router-dom';
import { TextField, MenuItem, FormControl, Select } from '@material-ui/core';
import ReactSelect from "react-select";
import { makeStyles } from '@material-ui/core/styles';
import { emailPlaceholders, getRecipientGroups, getTemplateById, postTemplate, sendBroadcast, sendIndividualEmails, saveAndSendEmail } from '../../store/actions/auth';
import { toast } from "react-toastify";
import _ from 'lodash';
import { useSelector } from 'react-redux';
import AlertDialogModal from '../common/AlertDialog';
import { OtherRecipientEmailsInput, countValidEmails, hasOtherRecipient } from './otherRecipients';
import { REPLY_TO_ERROR_MESSAGE, SENDER_FIELD_MAX_LENGTH, getSenderFields, getSenderPayload, isInvalidReplyToEmail } from './senderFields';
import { insertTextAtCursor } from './editorUtils';
import AttachmentsField from './AttachmentsField';
import { getAttachmentPayload, toAttachment } from './attachments';

const useStyles = makeStyles((theme) => ({
    formRow: {
        display: 'flex',
        alignItems: 'flex-start',
        marginBottom: theme.spacing(3),
        [theme.breakpoints.down('sm')]: {
            flexDirection: 'column',
        },
    },
    label: {
        width: 160,
        minWidth: 160,
        textAlign: 'right',
        paddingRight: theme.spacing(2),
        paddingTop: 9,
        fontWeight: 500,
        color: '#333',
        flexShrink: 0,
        [theme.breakpoints.down('sm')]: {
            width: '100%',
            minWidth: 'unset',
            textAlign: 'left',
            paddingBottom: theme.spacing(0.5),
            paddingRight: 0,
        },
    },
    fieldWrapper: {
        flex: 1,
    },
    select: {
        width: '100%',
        backgroundColor: '#fff',
    },
    textField: {
        width: '100%',
        backgroundColor: '#fff',
    },
    quillWrapper: {
        flex: 1,
    },
    resultSummary: {
        padding: theme.spacing(2),
        '& p': {
            margin: theme.spacing(0.5, 0),
            fontSize: 14,
        },
    },
}));

const ComposeTemplate = () => {
    const classes = useStyles();
    const [showLoader, setShowLoader] = React.useState(false);
    const [formData, setFormData] = React.useState({
        recipients: [],
        templateName: '',
        subject: '',
        recipientEmailsList: [],
        otherEmails: '',
        body: '',
        fromName: '',
        replyToEmail: '',
    });
    const [emailInput, setEmailInput] = React.useState('');
    const [attachments, setAttachments] = React.useState([]);
    const [uploadingAttachments, setUploadingAttachments] = React.useState(false);
    const [recipientList, setRecipientList] = React.useState([]);
    const [emailPlaceholderList, setEmailPlaceholderList] = React.useState([]);
    const [showAlert, setShowAlert] = React.useState(false);
    const [alertAction, setAlertAction] = React.useState(null);
    const [sendResult, setSendResult] = React.useState(null);
    const iframeRef = React.useRef(null);
    const { id } = useParams();
    const userDetails = useSelector((state) => state.auth.user);
    const navigate = useNavigate();

    const userRole = useMemo(() => {
        const roleName = (userDetails?.Value?.roleName || '').toString().trim().toLowerCase();
        const role = (userDetails?.Value?.role || '').toString().trim().toLowerCase();
        if (userDetails?.Value?.isSuperAdmin) return 'superadmin';
        if (roleName === 'admin' || role === 'admin') return 'admin';
        if (roleName === 'seller' || role === 'seller') return 'seller';
        if (roleName === 'warehouse manager' || role === 'warehouse manager') return 'warehousemanager';
        if (roleName === 'warehouse worker' || role === 'warehouse worker') return 'warehouseworker';
        return roleName || role;
    }, [userDetails]);

    const isSellerOrAdmin = useMemo(() => {
        return ['admin', 'seller'].includes(userRole);
    }, [userRole]);

    const canSaveTemplate = useMemo(() => ['superadmin', 'admin', 'seller'].includes(userRole), [userRole]);
    const canSendBroadcast = useMemo(() => userRole === 'superadmin', [userRole]);
    const canSendIndividual = useMemo(() => ['superadmin', 'admin', 'seller'].includes(userRole), [userRole]);
    const canSaveAndSend = useMemo(() => ['superadmin', 'admin', 'seller'].includes(userRole), [userRole]);
    const showSaveAndSend = canSaveAndSend && (Number(id) > 0 || formData.templateName.trim());

    const canSend = isSellerOrAdmin ? canSendIndividual : canSendBroadcast;

    const fetchTemplateDetails = useCallback(async (templateId) => {
        setShowLoader(true);
        try {
            const response = await getTemplateById(templateId);
            if (response?.data?.Status === 200) {
                const template = response.data.Entity;
                setFormData({
                    recipients: template.recipients || [],
                    recipientEmailsList: template.recipientEmailsList || [],
                    otherEmails: '',
                    templateName: template.name || '',
                    subject: template.subject || '',
                    body: template.body || '',
                    id: template.id,
                    ...getSenderFields(template),
                });
                writeToIframe(template.body || '');
                setAttachments((template.attachments || []).map(toAttachment));
            }
        } catch {
            toast.error('An error occurred while fetching template details.');
        } finally {
            setShowLoader(false);
        }
    }, []);

    const fetchemailPlaceholders = useCallback(async () => {
        try {
            const response = await emailPlaceholders();
            if (response?.data?.Status === 200) {
                setEmailPlaceholderList(response?.data?.EntityList || []);
            }
        } catch {
            toast.error('An error occurred while fetching email placeholders.');
        }
    }, []);

    const fetchRecipientList = useCallback(async () => {
        setShowLoader(true);
        try {
            const response = await getRecipientGroups();
            const listWithAllOption = response.data.EntityList.map(group => ({ ...group, value: group.id, label: `${group.name} - ${group.description}` }));
            setRecipientList(listWithAllOption);
        } catch {
            toast.error('An error occurred while fetching recipient groups.');
        } finally {
            setShowLoader(false);
        }
    }, []);

    useEffect(() => {
        fetchemailPlaceholders();
        fetchRecipientList();
    }, [fetchemailPlaceholders, fetchRecipientList]);

    useEffect(() => {
        if (id && Number(id) > 0) {
            fetchTemplateDetails(id);
        }
    }, [id, fetchTemplateDetails]);

    const handleChange = (field, value) => {
        setFormData((prev) => ({ ...prev, [field]: value }));
    };

    const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

    const showOtherEmails = !isSellerOrAdmin && hasOtherRecipient(formData.recipients);

    const addEmail = (email) => {
        if (!email) return;
        if (!isValidEmail(email)) {
            toast.error(`"${email}" is not a valid email address.`);
            return;
        }
        if (formData.recipientEmailsList.includes(email)) {
            setEmailInput('');
            return;
        }
        handleChange('recipientEmailsList', [...formData.recipientEmailsList, email]);
        setEmailInput('');
    };

    const removeEmail = (email) => {
        handleChange('recipientEmailsList', formData.recipientEmailsList.filter((e) => e !== email));
    };

    const handleEmailInputKeyDown = (e) => {
        if (e.key === 'Enter' || e.key === ',') {
            e.preventDefault();
            addEmail(emailInput.trim());
        }
    };

    const handleMultipleSelectChange = (value, field) => {
        handleChange(field, value);
    };

    const getIframeDocument = () => {
        const iframe = iframeRef.current;
        return iframe?.contentDocument || iframe?.contentWindow?.document;
    };

    const getIframeBody = useCallback(() => {
        const doc = getIframeDocument();
        return doc?.body?.innerHTML || '';
    }, []);

    const writeToIframe = useCallback((html) => {
        const iframe = iframeRef.current;
        if (!iframe) return;
        const doc = iframe.contentDocument || iframe.contentWindow?.document;
        if (!doc) return;
        doc.open();
        doc.write(`<!DOCTYPE html><html><head><style>body { margin: 0; padding: 0; font-family: Arial, sans-serif; }</style></head><body>${html}</body></html>`);
        doc.close();
        doc.designMode = 'on';
        doc.body.addEventListener('input', () => {
            handleChange('body', doc.body.innerHTML);
        });
    }, []);

    // A new template has nothing to load, so the editor must still be initialised to be editable.
    useEffect(() => {
        if (!(id && Number(id) > 0)) writeToIframe('');
    }, [id, writeToIframe]);

    const insertDynamicValue = (variable) => {
        if (!variable) return;
        const doc = getIframeDocument();
        if (!doc?.body) return;
        if (insertTextAtCursor(iframeRef.current, variable)) {
            handleChange('body', doc.body.innerHTML);
        }
    };

    const validateSaveForm = () => {
        if (!formData.templateName.trim()) {
            toast.error('Template name is required.');
            return false;
        }
        if (!formData.subject.trim()) {
            toast.error('Subject is required.');
            return false;
        }
        if (isInvalidReplyToEmail(formData.replyToEmail)) {
            toast.error(REPLY_TO_ERROR_MESSAGE);
            return false;
        }
        return true;
    };

    const validateForm = () => {
        const latestBody = getIframeBody();
        if (latestBody !== formData.body) {
            handleChange('body', latestBody);
        }
        if (!isSellerOrAdmin && (!formData.recipients || formData.recipients.length === 0)) {
            toast.error('At least one recipient is required.');
            return false;
        }
        if (showOtherEmails && countValidEmails(formData.otherEmails) === 0) {
            toast.error('Enter at least one valid email address for the "Other" recipients.');
            return false;
        }
        if (isSellerOrAdmin && formData.recipientEmailsList.length === 0) {
            toast.error('Recipient email(s) are required for your role.');
            return false;
        }
        if (!formData.templateName.trim()) {
            toast.error('Template name is required.');
            return false;
        }
        if (!formData.subject.trim()) {
            toast.error('Subject is required.');
            return false;
        }
        const bodyContent = latestBody || formData.body || '';
        const strippedBody = bodyContent.replaceAll(/<[^>]*>/g, '').trim();
        if (!strippedBody) {
            toast.error('Body is required.');
            return false;
        }
        if (isInvalidReplyToEmail(formData.replyToEmail)) {
            toast.error(REPLY_TO_ERROR_MESSAGE);
            return false;
        }
        return true;
    };

    const validateSaveAndSendForm = () => {
        if (!formData.templateName.trim()) {
            toast.error('Template name is required for Save & Send.');
            return false;
        }
        return validateForm();
    };

    const flushEmailInput = () => {
        if (emailInput.trim()) {
            if (!isValidEmail(emailInput.trim())) {
                toast.error(`"${emailInput.trim()}" is not a valid email address.`);
                return false;
            }
            addEmail(emailInput.trim());
            return false;
        }
        return true;
    };

    const buildPayload = () => {
        const currentBody = getIframeBody() || formData.body;
        let payload = {
            sendMode: 'Broadcast',
            subject: formData.subject.trim(),
            body: currentBody,
            recipientGroupIds: _.map(formData.recipients, 'value'),
            templateId: formData.id || 0,
            templateName: formData.templateName,
            fromName: formData.fromName.trim() || undefined,
            replyToEmail: formData.replyToEmail.trim() || undefined,
            attachments: getAttachmentPayload(attachments),
        };
        if (isSellerOrAdmin) {
            payload = {
                ...payload,
                sendMode: 'Individual',
                recipientEmails: formData.recipientEmailsList.join(', '),
            };
        }
        if (!isSellerOrAdmin && showOtherEmails) {
            payload = { ...payload, recipientEmails: formData.otherEmails.trim() };
        }
        return payload;
    };

    // --- Save Template ---
    const handleSaveTemplate = async () => {
        if (!validateSaveForm()) return;
        setShowLoader(true);
        try {
            const currentBody = getIframeBody() || formData.body;
            const payload = {
                id: formData.id || 0,
                name: formData.templateName,
                subject: formData.subject,
                body: currentBody,
                recipients: formData.recipients,
                attachments: getAttachmentPayload(attachments),
                ...getSenderPayload(formData),
            };
            const response = await postTemplate(payload);
            if (response?.data?.Status === 200) {
                toast.success('Template saved successfully!');
                navigate('/email-templates');
            } else {
                toast.error(response?.data?.Message?.AppStatusDescription || 'Failed to save template.');
            }
        } catch {
            toast.error('An error occurred while saving the template.');
        } finally {
            setShowLoader(false);
        }
    };

    // --- Send ---
    const handleSendClick = () => {
        if (!flushEmailInput()) return;
        if (!validateForm()) return;
        setAlertAction('send');
        setShowAlert(true);
    };

    const handleSend = async () => {
        setShowAlert(false);
        setShowLoader(true);
        try {
            const payload = buildPayload();
            const response = await (isSellerOrAdmin ? sendIndividualEmails(payload) : sendBroadcast(payload));
            if (response?.data?.Status === 200) {
                toast.success('Email sent successfully!');
                setSendResult(response.data.Entity || response.data);
            } else {
                toast.error(response?.data?.Message?.AppStatusDescription || 'Failed to send email.');
            }
        } catch {
            toast.error('An error occurred while sending the email.');
        } finally {
            setShowLoader(false);
        }
    };

    // --- Save & Send ---
    const handleSaveAndSendClick = () => {
        if (!flushEmailInput()) return;
        if (!validateSaveAndSendForm()) return;
        setAlertAction('saveAndSend');
        setShowAlert(true);
    };

    const handleSaveAndSend = async () => {
        setShowAlert(false);
        setShowLoader(true);
        try {
            const payload = buildPayload();
            const response = await saveAndSendEmail(payload);
            if (response?.data?.Status === 200) {
                toast.success('Template saved and email sent successfully!');
                setSendResult(response.data.Entity || response.data);
            } else {
                toast.error(response?.data?.Message?.AppStatusDescription || 'Failed to save & send.');
            }
        } catch {
            toast.error('An error occurred while saving and sending.');
        } finally {
            setShowLoader(false);
        }
    };

    const handleAlertConfirm = () => {
        if (alertAction === 'send') handleSend();
        else if (alertAction === 'saveAndSend') handleSaveAndSend();
    };

    return (
        <main>
            <div className="main__container">
                <div className="main__tile_heading cus-btn-include-sec">
                    <span>
                        {Number(id) ? 'Edit' : 'Create'} Email Templates
                        <div className='infoPermission'>Manage the email templates used for various notifications and communications.</div>
                    </span>
                    <div className="top_right_button_container">
                        <div className="top_right_button_container">
                            {canSaveTemplate && (
                                <button disabled={showLoader || uploadingAttachments} className="btn cus-seconday-bg-btn btn mr-2" onClick={handleSaveTemplate}>
                                    Save Template
                                </button>
                            )}
                            {showSaveAndSend && (
                                <button disabled={showLoader || uploadingAttachments} className="btn cus-seconday-bg-btn btn mr-2" onClick={handleSaveAndSendClick}>
                                    Save &amp; Send
                                </button>
                            )}
                            {canSend && (
                                <button disabled={showLoader || uploadingAttachments} className="btn cus-seconday-bg-btn btn mr-2" onClick={handleSendClick}>
                                    Send Email
                                </button>
                            )}
                            <button className="btn cus-primary-transparent-btn bckbtn" onClick={() => navigate('/email-templates')}>
                                Back
                            </button>
                        </div>
                    </div>
                </div>
                {showLoader && <LoaderSpinner />}
                <div className="cus-page-content-sec">
                    <div className='content-container pt-4'>
                        <div className={classes.formRow}>
                            <div className={classes.label}>Select Recipients:</div>
                            {isSellerOrAdmin ? (
                                <div className={classes.fieldWrapper}>
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, padding: '6px 10px', border: '1px solid #c4c4c4', borderRadius: 4, minHeight: 40, alignItems: 'center', backgroundColor: '#fff', cursor: 'text' }}>
                                        {formData?.recipientEmailsList?.map((email) => (
                                            <span key={email} style={{ display: 'inline-flex', alignItems: 'center', background: '#e0e0e0', borderRadius: 16, padding: '2px 10px', fontSize: 13 }}>
                                                {email}
                                                <button className='sortBtn' onClick={() => removeEmail(email)} style={{ padding:'4px', marginLeft: 6, cursor: 'pointer', fontSize: 16, lineHeight: 1, color: '#555' }}>&times;</button>
                                            </span>
                                        ))}
                                        <input
                                            type="text"
                                            value={emailInput}
                                            onChange={(e) => setEmailInput(e.target.value)}
                                            onKeyDown={handleEmailInputKeyDown}
                                            onBlur={() => emailInput.trim() && addEmail(emailInput.trim())}
                                            placeholder={formData.recipientEmailsList.length === 0 ? 'Type email and press Enter or comma' : ''}
                                            style={{ border: 'none', outline: 'none', flex: 1, minWidth: 180, fontSize: 14, background: 'transparent' }}
                                        />
                                    </div>
                                </div>
                            ) : (
                                <div className={classes.fieldWrapper}>
                                    <FormControl variant="outlined" className={classes.select} size="small">
                                        <ReactSelect
                                            value={formData.recipients}
                                            isMulti
                                            options={recipientList}
                                            onChange={(value) => handleMultipleSelectChange(value, 'recipients')}
                                        />
                                    </FormControl>
                                    {showOtherEmails && (
                                        <OtherRecipientEmailsInput
                                            value={formData.otherEmails}
                                            onChange={(value) => handleChange('otherEmails', value)}
                                            disabled={showLoader}
                                        />
                                    )}
                                </div>
                            )}
                        </div>

                        <div className={classes.formRow}>
                            <div className={classes.label}>Template Name:</div>
                            <div className={classes.fieldWrapper}>
                                <TextField
                                    variant="outlined"
                                    size="small"
                                    className={classes.textField}
                                    value={formData.templateName}
                                    onChange={(e) => handleChange('templateName', e.target.value)}
                                />
                            </div>
                        </div>

                        <div className={classes.formRow}>
                            <div className={classes.label}>From Name:</div>
                            <div className={classes.fieldWrapper}>
                                <TextField
                                    variant="outlined"
                                    size="small"
                                    className={classes.textField}
                                    value={formData.fromName}
                                    onChange={(e) => handleChange('fromName', e.target.value)}
                                    placeholder="e.g., {Name} from {CompanyName}"
                                    inputProps={{ maxLength: SENDER_FIELD_MAX_LENGTH }}
                                />
                            </div>
                        </div>

                        <div className={classes.formRow}>
                            <div className={classes.label}>Reply-To Email:</div>
                            <div className={classes.fieldWrapper}>
                                <TextField
                                    variant="outlined"
                                    size="small"
                                    className={classes.textField}
                                    value={formData.replyToEmail}
                                    onChange={(e) => handleChange('replyToEmail', e.target.value)}
                                    placeholder="e.g., name@example.com"
                                    type="email"
                                    inputProps={{ maxLength: SENDER_FIELD_MAX_LENGTH }}
                                />
                            </div>
                        </div>

                        <div className={classes.formRow}>
                            <div className={classes.label}>Subject:</div>
                            <div className={classes.fieldWrapper}>
                                <TextField
                                    variant="outlined"
                                    size="small"
                                    className={classes.textField}
                                    value={formData.subject}
                                    onChange={(e) => handleChange('subject', e.target.value)}
                                />
                            </div>
                        </div>

                        <div className={classes.formRow}>
                            <div className={classes.label}>Body:</div>
                            <div className={classes.quillWrapper}>
                                {!isSellerOrAdmin && (
                                    <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8, gap: 8 }}>
                                        <span style={{ fontSize: 13, color: '#555', fontWeight: 500 }}>Insert Variable:</span>
                                        <FormControl variant="outlined" size="small" style={{ minWidth: 200 }}>
                                            <Select
                                                displayEmpty
                                                value=""
                                                onChange={(e) => {
                                                    insertDynamicValue(e.target.value);
                                                }}
                                                renderValue={() => 'Select a variable'}
                                            >
                                                {emailPlaceholderList.map((v) => (
                                                    <MenuItem key={v.value} value={v.value}>
                                                        {v.label} &nbsp;<span style={{ color: '#888', fontSize: 12 }}>{v.value}</span>
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </div>
                                )}
                                <iframe
                                    ref={iframeRef}
                                    title="Email Body Editor"
                                    style={{
                                        width: '100%',
                                        minHeight: 400,
                                        border: '1px solid #c4c4c4',
                                        borderRadius: 4,
                                        backgroundColor: '#fff',
                                    }}
                                />
                            </div>
                        </div>

                        <div className={classes.formRow}>
                            <div className={classes.label}>Attachments:</div>
                            <div className={classes.fieldWrapper}>
                                <AttachmentsField
                                    attachments={attachments}
                                    onChange={setAttachments}
                                    onUploadingChange={setUploadingAttachments}
                                    disabled={showLoader}
                                    templateId={formData.id || undefined}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Send / Save & Send Confirmation */}
            <AlertDialogModal
                modalShow={showAlert}
                handleCancelDeleteAlert={() => { setShowAlert(false); setAlertAction(null); }}
                handleYesDeleteAlert={handleAlertConfirm}
                headingText={alertAction === 'saveAndSend' ? 'Confirm Save & Send' : 'Confirm Send'}
                messageText={alertAction === 'saveAndSend'
                    ? 'Are you sure you want to save the template and send this email?'
                    : 'Are you sure you want to send this email?'}
            />

            {/* Send Result Summary */}
            <AlertDialogModal
                modalShow={!!sendResult}
                handleCancelDeleteAlert={() => setSendResult(null)}
                handleYesDeleteAlert={() => setSendResult(null)}
                headingText="Send Result"
                default
                messageText={sendResult ? (
                    <div className={classes.resultSummary}>
                        <p><strong>Total Recipients:</strong> {sendResult.totalRecipients ?? 'N/A'}</p>
                        <p><strong>Delivered:</strong> {sendResult.deliveredCount ?? 'N/A'}</p>
                        <p><strong>Failed:</strong> {sendResult.failedCount ?? 'N/A'}</p>
                        <p><strong>Status:</strong> {sendResult.status ?? 'Completed'}</p>
                        {sendResult.invalidEmails?.length > 0 && (
                            <p><strong>Skipped invalid emails:</strong> {sendResult.invalidEmails.join(', ')}</p>
                        )}
                    </div>
                ) : null}
            />

        </main>
    );
};

export default ComposeTemplate;